import type { BoardData, Item } from '../model/schema.js';
import { nodeLabel } from '../trace/summary.js';
import type { NodeRef } from '../trace/types.js';

/** Item kinds that are patch points (where channels are physically plugged in). Sub-cores are only extensions. */
export const PATCH_KINDS: ReadonlySet<Item['kind']> = new Set(['stagebox', 'console']);

/** "Brand Model" of an item, empty if unknown. */
export function brandModel(item: Item | undefined): string {
  return [item?.brand, item?.model].filter((s) => s !== undefined && s !== '').join(' ');
}

/** Label of a port node (`SB1 In 5`). */
export function portLabel(data: BoardData, itemId: string, portId: string): string {
  return nodeLabel(data, { type: 'port', itemId, portId });
}

/** True if the node is an input port of a patch point (stagebox, console). */
export function isPatchInput(data: BoardData, node: NodeRef): boolean {
  if (node.type !== 'port') return false;
  const item = data.items[node.itemId];
  const port = item?.ports?.find((p) => p.id === node.portId);
  return item !== undefined && PATCH_KINDS.has(item.kind) && port?.direction === 'in';
}

/** Compares by number, then by string (for stable row order). */
export function byNumber<T extends { number: number; rowKey: string }>(a: T, b: T): number {
  return a.number - b.number || a.rowKey.localeCompare(b.rowKey);
}

/** Rounds to two decimals. */
export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
